import { useLang } from "../contexts/LanguageContext";
import { Star, Quote } from "lucide-react";

const TESTIMONIALS = [
    {
        name: "Marc Ferrer",
        role_es: "MT-09 SP · Palma",
        role_en: "MT-09 SP · Palma",
        img: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=240&h=240&fit=crop&crop=faces",
        text_es: "Compré mi MT-09 SP en LS39 y el trato fue impecable de principio a fin. Asesoramiento técnico real, no comercial. La entrega con primera revisión incluida es un detalle que se nota.",
        text_en: "I bought my MT-09 SP at LS39 and the experience was impeccable from start to finish. Real technical advice, not just sales. Delivery with first service included is a real plus.",
    },
    {
        name: "Laura Vidal",
        role_es: "NMAX 125 · Llucmajor",
        role_en: "NMAX 125 · Llucmajor",
        img: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=240&h=240&fit=crop&crop=faces",
        text_es: "Mi primera moto. El equipo de LS39 me dedicó dos horas para encontrar la NMAX perfecta para mi día a día en Palma. Cero presión y mucha paciencia.",
        text_en: "My first bike. The LS39 team spent two hours helping me find the perfect NMAX for my Palma commute. Zero pressure, lots of patience.",
    },
    {
        name: "Toni Mas",
        role_es: "Ténéré 700 · Sóller",
        role_en: "Ténéré 700 · Sóller",
        img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=240&h=240&fit=crop&crop=faces",
        text_es: "Llevo dos años con la Ténéré preparada por LS39 para off-road. Mantenimiento perfecto, diagnóstico siempre acertado y precios honestos en recambios originales.",
        text_en: "I've had my LS39-prepped Ténéré for two years now. Perfect maintenance, always accurate diagnostics and honest pricing on original parts.",
    },
    {
        name: "Cristina Coll",
        role_es: "TMAX Tech MAX · Magaluf",
        role_en: "TMAX Tech MAX · Magaluf",
        img: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=240&h=240&fit=crop&crop=faces",
        text_es: "Pasé del coche al TMAX y no me arrepiento. La prueba previa en LS39 fue clave para tomar la decisión. Servicio post-venta de 10.",
        text_en: "I switched from car to TMAX and have no regrets. The test ride at LS39 was key to my decision. After-sales service is top notch.",
    },
    {
        name: "Joan Pons",
        role_es: "R7 · Manacor",
        role_en: "R7 · Manacor",
        img: "https://images.unsplash.com/photo-1531427186611-ecfd6d936c79?w=240&h=240&fit=crop&crop=faces",
        text_es: "Compré la R7 para circuito y carretera. El taller me la preparó con escape homologado y setup específico. Profesionales de verdad.",
        text_en: "I bought the R7 for track and road. The workshop set it up with a homologated exhaust and a specific suspension setup. True professionals.",
    },
    {
        name: "Aina Bennàssar",
        role_es: "Tricity 300 · Palma",
        role_en: "Tricity 300 · Palma",
        img: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=240&h=240&fit=crop&crop=faces",
        text_es: "Volví a la moto después de 15 años y la Tricity 300 me dio confianza desde el primer momento. LS39 me acompañó incluso en la primera salida.",
        text_en: "I returned to riding after 15 years and the Tricity 300 gave me confidence from day one. LS39 even joined me on the first ride.",
    },
];

export default function Testimonials({ limit = 6 }) {
    const { lang } = useLang();
    const t = lang === "es" ? {
        overline: "Clientes LS39",
        title: "Lo que dicen nuestros riders",
        sub: "Más de 1.400 motos vendidas, miles de revisiones y cero compromiso en la calidad del servicio.",
    } : {
        overline: "LS39 Customers",
        title: "What our riders say",
        sub: "Over 1,400 bikes sold, thousands of services and zero compromise on quality.",
    };

    const items = TESTIMONIALS.slice(0, limit);

    return (
        <section
            className="border-t border-black/10 bg-[--ls-paper]"
            data-testid="testimonials-section"
        >
            <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
                <p className="text-overline">{t.overline}</p>
                <h2 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">
                    {t.title}
                </h2>
                <p className="mt-4 max-w-2xl text-base text-neutral-500">{t.sub}</p>

                <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {items.map((item, i) => (
                        <article
                            key={i}
                            data-testid={`testimonial-card-${i}`}
                            className="glass relative p-7 flex flex-col"
                        >
                            <Quote
                                size={22}
                                style={{ color: "var(--ls-terracotta)" }}
                                className="opacity-70"
                            />

                            <p className="mt-5 text-sm text-neutral-700 leading-relaxed flex-grow">
                                "{lang === "es" ? item.text_es : item.text_en}"
                            </p>

                            <div className="mt-6 flex items-center gap-1" data-testid={`testimonial-stars-${i}`}>
                                {Array.from({ length: 5 }).map((_, k) => (
                                    <Star
                                        key={k}
                                        size={14}
                                        fill="var(--ls-terracotta)"
                                        stroke="var(--ls-terracotta)"
                                    />
                                ))}
                            </div>

                            <div className="mt-5 flex items-center gap-4 border-t border-black/10 pt-5">
                                <img
                                    src={item.img}
                                    alt={item.name}
                                    loading="lazy"
                                    className="h-12 w-12 rounded-full object-cover border border-black/15"
                                />
                                <div>
                                    <p className="font-display text-base font-semibold text-neutral-900">
                                        {item.name}
                                    </p>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                                        {lang === "es" ? item.role_es : item.role_en}
                                    </p>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}
